import { Prompt, Prompts } from '@Domains/Cart/Components/Prompt/types'
import { AddToCartProductInput } from '@Generated/graphql'

export interface PromptStep {
  /** Position in the add-to-cart products input. */
  inputIndex: number
  key: string
  /** engrave/deboss, once the step's prompt has disclosed it. */
  personalizationType?: string
  type: 'personalization' | 'size'
}

export const getSelectedPersonalizationType = (prompt: Prompt): string | false => {
  if (prompt.__typename !== 'AddToCartPromptPersonalization') {
    return false
  }

  if (prompt.selectedPersonalization?.masterVariant?.type?.__typename !== 'BaseAttribute') {
    return 'unknown'
  }

  return (prompt.selectedPersonalization.masterVariant.type.value as any)?.key || 'unknown'
}

/**
 * Reconciles the flow's steps from the outstanding prompts — every response lists all
 * unresolved prompts, so the server itself types each pending selection. Size steps
 * are all disclosed in the first response (the resolver checks variants before parent
 * linkage); a missing_parent error on a child awaiting selection is a personalization
 * step by exhaustion (a size-needing child would have prompted SelectVariant instead).
 * Completed steps are kept; steps that are neither reached nor still prompted for
 * resolved silently and are dropped.
 */
export const reconcilePromptSteps = (
  previousSteps: PromptStep[],
  prompts: Prompts,
  promptsOrigin: AddToCartProductInput[] | null | undefined,
  activePromptIndex: number,
): PromptStep[] => {
  const steps = previousSteps.map(step => ({ ...step }))
  const byIndex = new Map(steps.map(step => [step.inputIndex, step]))

  const isChildAwaitingSelection = (index: number) => {
    const item = promptsOrigin?.[index]
    if (!item) {
      return false
    }
    const isChild = item.parentIndex !== undefined && item.parentIndex !== null
    return isChild && !item.personalization?.text
  }

  const upsert = (index: number, type: PromptStep['type'] | null, personalizationType?: string) => {
    const existing = byIndex.get(index)
    if (existing) {
      if (type) {
        existing.type = type
      }
      if (personalizationType) {
        existing.personalizationType = personalizationType
      }
      return
    }

    const step: PromptStep = { inputIndex: index, key: `step-${index}`, type: type || 'personalization' }
    if (personalizationType) {
      step.personalizationType = personalizationType
    }
    steps.push(step)
    byIndex.set(index, step)
  }

  prompts.forEach((prompt) => {
    if (!('index' in prompt) || typeof prompt.index !== 'number') {
      return
    }

    if (prompt.__typename === 'AddToCartPromptSelectVariant') {
      upsert(prompt.index, 'size')
    } else if (prompt.__typename === 'AddToCartPromptPersonalization') {
      // 'unknown' = data missing this response; never overwrite a disclosed kind.
      const personalizationType = getSelectedPersonalizationType(prompt)
      upsert(prompt.index, 'personalization', personalizationType && personalizationType !== 'unknown' ? personalizationType : undefined)
    } else if (prompt.__typename === 'AddToCartPromptError' && prompt.code === 'missing_parent' && isChildAwaitingSelection(prompt.index)) {
      upsert(prompt.index, null)
    }
  })

  const activeStepPosition = steps.findIndex(step => step.inputIndex === activePromptIndex)
  return steps.filter((step, position) => (
    (activeStepPosition !== -1 && position <= activeStepPosition)
    || prompts.some(prompt => 'index' in prompt && prompt.index === step.inputIndex)
  ))
}
